import * as _ from 'lodash';
import * as React from 'react';
import { DataShape, Field } from 'redux-form';
import { hashHistory } from 'react-router';
import { css, StyleSheet } from 'aphrodite';
import { colors, combineStyles, fonts, media, mixins } from 'consumer-components';
import { inputFormatters } from 'ui-common';

import { CreditScore } from 'login-utils/onboardingForm';
import { paneFormStyles } from 'login-components/Common/PaneLayout/paneStyles';
import { UpfrontRate } from './upfrontRatesUtils';
import ChevronSVG from 'login-components/Common/svg/ChevronSVG';
import PrimaryButton from 'login-components/Common/Buttons/PrimaryButton';
import States from 'login-constants/states';
import UpfrontRatesTable from './UpfrontRatesTable/UpfrontRatesTable';

type FormEventHandler = (event: React.FormEvent<HTMLFormElement>) => void;

export interface UpfrontRatesProps extends ReactRouter.RouteComponentProps<{}, {}> {
  disabled: boolean;
  disclaimer: string | null;
  hasError: boolean;
  inputs: FormInput[];
  latestResultsTimestamp: number | null;
  loading: boolean;
  onSubmit: FormEventHandler;
  submittedValues: DataShape;
  upfrontRates: UpfrontRate[];
  isRefinance: boolean;
}

interface UpfrontRatesState {
  expanded: boolean;
}

interface SentenceParams {
  propertyValue: string;
  purchasePrice: string;
  loanAmount: string;
  cashDownPayment: string;
  estimatedCreditScore: string;
  propertyCounty: {
    countyName: string;
    stateCode: string;
  };
}

const styles = StyleSheet.create({
  upfrontRates: {
    ...fonts.Roboto,
    maxWidth: 540,
  },
  tagline: {
    color: colors.darkerGray,
    fontSize: 14,
    lineHeight: '1.4em',
    marginRight: 4,
  },
  bold: {
    fontWeight: fonts.weight.bold,
  },
  truncatedText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  expandButton: {
    backgroundColor: 'white', // mobile buttons have a default grey background
    border: 'none',
    cursor: 'pointer',
    flexShrink: 0, // IE11 bug to prevent button width from collapsing
    height: 24, // need to match the height of the svg
    outline: 'none',
    padding: 0,
  },
  formContainer: combineStyles(mixins.border(2, 'solid', colors.white), mixins.borderRadius(2), {
    ...mixins.padding(16),
    boxShadow: mixins.boxShadow(0, 1, 6, 0, mixins.rgba(0, 0, 0, 0.2)),
    marginBottom: 32,
  }),
  formHeader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  formBody: {
    display: 'none',
  },
  expandedFormBody: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: 20,
    [media.mediumView]: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
    },
  },
  fieldWrapper: {
    marginBottom: 20,
    [media.mediumView]: {
      width: '49%',
    },
  },
  updateButton: {
    marginLeft: 'auto', // align button to right side of form
    marginTop: 4,
  },
  latestResultsTimestampWrapper: {
    paddingTop: 16,
  },
  subtext: {
    ...fonts.Roboto,
    color: colors.clayPlus3,
    fontSize: 12,
  },
  continueButton: {
    ...mixins.marginVertical(60),
  },
});

const transitionStyles = StyleSheet.create({
  heightTransitionWrapper: {
    maxHeight: 0,
    opacity: 0,
    overflow: 'hidden',
    transition: 'max-height 0.4s ease-out, opacity 0.4s linear',
  },
  expandedWrapper: {
    // maxHeight must be slightly greater than the max possible height of the form so it's is not cut off.
    // In contrast, an overly large maxHeight will cause the animation to be janky.
    maxHeight: 600,
    opacity: 1,
    overflow: 'initial',
    [media.mediumView]: {
      maxHeight: 400,
    },
  },
  rotatingArrow: {
    transformOrigin: 'center',
    transition: 'transform 0.4s ease-out',
  },
  rotated: {
    transformOrigin: 'center',
    transform: 'rotate(180deg)',
  },
});

const { DATE, MONEY } = inputFormatters;
const creditScoreToText: Record<CreditScore, string> = {
  740: '740 and above',
  720: '700 to 739',
  680: '660 to 699',
  640: '620 to 659',
  619: 'below 620',
};

export default class UpfrontRates extends React.Component<UpfrontRatesProps, UpfrontRatesState> {
  state: UpfrontRatesState = {
    expanded: false,
  };

  goToMilestones = () => {
    const { location } = this.props;
    const search = location!.search || '';
    hashHistory.push(`${States.MILESTONES}${search}`);
  };

  generateSentence = (isRefinance: boolean, params: SentenceParams) => {
    const { propertyValue, purchasePrice, propertyCounty, loanAmount, cashDownPayment, estimatedCreditScore } = params;

    if (
      _.isEmpty(propertyCounty) ||
      (isRefinance && (_.isNil(loanAmount) || _.isNil(propertyValue))) ||
      (!isRefinance && (_.isNil(cashDownPayment) || _.isNil(purchasePrice))) ||
      _.isNil(estimatedCreditScore)
    ) {
      // Note: This should never be the case because the user must pre-fill all values in short-form before entering
      // upfront rates.
      return 'edit inputs in the form to see possible loan options.';
    }

    const formattedPurchasePrice = MONEY.formatRoundedWithDollarSign(purchasePrice);
    const formattedPropertyValue = MONEY.formatRoundedWithDollarSign(propertyValue);
    const formattedCashDownPayment = MONEY.formatRoundedWithDollarSign(cashDownPayment);
    const formattedLoanAmount = MONEY.formatRoundedWithDollarSign(loanAmount);
    const formattedCreditScore = creditScoreToText[estimatedCreditScore];
    const { countyName, stateCode } = propertyCounty;
    if (isRefinance) {
      return `refinancing a ${formattedPropertyValue} home in ${countyName}, ${stateCode} with \
      ${formattedLoanAmount} loan amount and estimated ${formattedCreditScore} credit score.`;
    } else {
      return `buying a ${formattedPurchasePrice} home in ${countyName}, ${stateCode} with \
      ${formattedCashDownPayment} down payment and estimated ${formattedCreditScore} credit score.`;
    }
  };

  renderTagline = () => {
    const { submittedValues, isRefinance } = this.props;
    const { expanded } = this.state;
    const sentence = this.generateSentence(isRefinance, submittedValues as SentenceParams);

    return (
      <p
        aria-expanded={expanded}
        className={css(styles.tagline, !expanded && styles.truncatedText)}
        data-it="UpfrontRates-tagline"
      >
        <span className={css(styles.bold)}>Inputs:</span> {sentence}
      </p>
    );
  };

  renderForm = () => {
    const { disabled, inputs, loading, onSubmit } = this.props;
    const { expanded } = this.state;

    return (
      <form
        aria-expanded={expanded}
        className={css(styles.formBody, expanded && styles.expandedFormBody)}
        hidden={!expanded}
        noValidate
        onSubmit={onSubmit}
      >
        {inputs.map(input => (
          <div className={css(styles.fieldWrapper)} key={input.name}>
            <Field {...input} />
          </div>
        ))}
        <div className={css(paneFormStyles.buttonDisplay, styles.updateButton)}>
          <PrimaryButton
            data-it="UpfrontRates-updateRates"
            disabled={disabled}
            fontFamily="Roboto"
            isWide={true}
            text={loading ? 'Fetching rates' : 'Update rates'}
          />
        </div>
      </form>
    );
  };

  toggleForm = () => this.setState(state => ({ expanded: !state.expanded }));

  render() {
    const { disclaimer, hasError, latestResultsTimestamp, loading, onSubmit, upfrontRates } = this.props;
    const { expanded } = this.state;

    return (
      <div className={css(styles.upfrontRates)}>
        <div className={css(styles.formContainer)}>
          <div className={css(styles.formHeader)}>
            {this.renderTagline()}
            <button
              aria-expanded={expanded}
              aria-label="Open to edit inputs"
              className={css(styles.expandButton, transitionStyles.rotatingArrow, expanded && transitionStyles.rotated)}
              data-it="UpfrontRates-expand"
              onClick={this.toggleForm}
            >
              <ChevronSVG />
            </button>
          </div>
          <div
            className={css(transitionStyles.heightTransitionWrapper, expanded && transitionStyles.expandedWrapper)}
            data-it="UpfrontRates-form"
          >
            {this.renderForm()}
          </div>
        </div>
        <UpfrontRatesTable
          hasError={hasError}
          loading={loading}
          onRefreshClick={onSubmit}
          upfrontRates={upfrontRates}
        />
        {latestResultsTimestamp && !_.isEmpty(upfrontRates) ? (
          <div className={css(styles.latestResultsTimestampWrapper)} data-it="UpfrontRates-latestResultsTimestamp">
            <p className={css(styles.subtext)}>
              Rates and fees as of {DATE.formatBrowserLocalMoment(latestResultsTimestamp)}. See important disclaimer
              below.
            </p>
          </div>
        ) : null}
        <div className={css(styles.continueButton, paneFormStyles.buttonDisplay)}>
          <PrimaryButton
            isWide={true}
            text="Continue"
            fontFamily="Roboto"
            data-it="UpfrontRates-continue"
            onClick={this.goToMilestones}
          />
        </div>
        {disclaimer && (
          <p className={css(styles.subtext)} data-it="UpfrontRates-disclaimer">
            {disclaimer}
          </p>
        )}
      </div>
    );
  }
}
